import React from "react";
import "./LegalPages.css";

const RETENTION_ROWS = [
    {
        data: "Compte (email pro, mot de passe, pseudonyme)",
        status: "Donnée personnelle",
        duration: "Conservé tant que le compte est actif",
    },
    {
        data: "Contenus publiés (posts, groupes de discussion)",
        status: "Pseudonymisé (le lien pseudo ↔ compte existe en base)",
        duration: "Conservé le temps du mandat électif en cours, puis anonymisé ou supprimé",
    },
    {
        data: "Réponse individuelle au pouls quotidien (mini-sondage)",
        status: "Peut être rendue réellement anonyme",
        duration: "Suppression de la réponse brute dès son agrégation — aucune conservation individuelle",
    },
    {
        data: "Résultats QVT / GHQ-12",
        status: "Anonyme par conception (pas de compte requis)",
        duration: "Sans objet — aucun identifiant n'est stocké",
    },
    {
        data: "Échanges du canal confidentiel",
        status: "Donnée personnelle sensible",
        duration: "Conservé le temps du traitement du dossier par le représentant",
    },
    {
        data: "Journaux techniques et de sécurité — adresse IP, date et heure de connexion, événements de sécurité et erreurs techniques",
        status: "Sécurité, prévention des abus et diagnostic des incidents — intérêt légitime du SAPPM, article 6.1.f du RGPD",
        duration: "6 mois, sauf nécessité particulière liée à un incident ou obligation légale",
    },
];

export default function ConfidentialitePage({onBack}) {
    return (
        <div className="legal-page">
            <div className="legal-wrapper">
                {onBack && (
                    <button className="legal-btn-back" onClick={onBack}>← Retour à la page d'accueil</button>
                )}

                <h1 className="legal-title">Confidentialité &amp; RGPD</h1>

                <p className="legal-paragraph">
                    Paroles d'Agents traite des données sensibles (état de bien-être, échanges parfois liés à des situations de souffrance au travail). Voici comment elles sont protégées.
                </p>

                <h2 className="legal-section-title">Qui a accès à quoi</h2>
                <ul className="legal-list">
                    <li>Vos publications, sondages et messages sont rattachés à un pseudonyme (animal + adjectif), jamais affichés avec votre nom sauf si vous choisissez explicitement le mode "identifié".</li>
                    <li>Votre hiérarchie n'a aucun accès à la plateforme, à vos contenus ni à votre identité.</li>
                    <li>Seuls les représentants du personnel élus du SAPPM administrant la plateforme peuvent, dans le cadre du canal confidentiel dédié, être mis en lien avec vous — toujours sous votre pseudonyme, et seulement si vous sollicitez ce canal.</li>
                </ul>

                <h2 className="legal-section-title">Ce qui est agrégé, jamais individuel</h2>
                <ul className="legal-list">
                    <li>Le tableau de bord "Ambiance collective" n'affiche aucun résultat en dessous d'un seuil minimum de répondant·es, pour qu'aucune réponse individuelle ne soit identifiable.</li>
                    <li>Ces données agrégées et anonymisées servent uniquement à documenter des alertes collectives (ex. saisine FSSSCT, point d'ordre du jour en CSA) — jamais à évaluer un agent en particulier.</li>
                </ul>

                <h2 className="legal-section-title">Base légale et finalité</h2>
                <p className="legal-paragraph">La base légale n'est pas unique — elle dépend de la nature de la donnée :</p>
                <ul className="legal-list">
                    <li><strong>Contenus du réseau</strong> (posts, groupes de discussion, canal confidentiel, compte) : article 6.1.e RGPD (mission d'intérêt public), rattaché au statut de représentant du personnel élu de la fonction publique d'État (CGFP) exercé par le SAPPM.</li>
                    <li><strong>Questionnaire GHQ-12</strong> (données de santé, catégorie particulière au sens de l'art. 9 RGPD) : base légale retenue — consentement explicite (art. 9.2.a), recueilli par un texte clair avant le démarrage du test.</li>
                    <li>Finalité : recueillir la parole des agent·es, mesurer le climat de travail de façon anonyme et alerter les instances compétentes (FSSSCT, CSA) en cas de besoin.</li>
                </ul>

                <h2 className="legal-section-title">Durée de conservation</h2>
                <p className="legal-paragraph">
                    Le SAPPM ne conserve aucune donnée personnelle au-delà de ce qui est strictement nécessaire au fonctionnement du réseau d'échange :
                </p>
                <table className="legal-table">
                    <thead>
                        <tr><th>Donnée</th><th>Statut</th><th>Durée</th></tr>
                    </thead>
                    <tbody>
                        {RETENTION_ROWS.map(row => (
                            <tr key={row.data}>
                                <td>{row.data}</td>
                                <td>{row.status}</td>
                                <td>{row.duration}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <p className="legal-paragraph">
                    L'hébergeur peut également conserver certaines données de connexion pour satisfaire aux obligations légales qui lui sont applicables. Les modalités de ces traitements relèvent de sa propre politique de confidentialité et des dispositions légales relatives à l'hébergement.
                </p>
                <p className="legal-paragraph">
                    Paroles d'Agents n'utilise aucun cookie publicitaire ni de suivi individuel. Seuls les mécanismes strictement nécessaires au fonctionnement, à l'authentification et à la sécurité du service peuvent être utilisés. L'audience du site est mesurée de façon anonyme (Matomo), sans cookie et sans identification des personnes ; vous pouvez refuser ce suivi depuis la page Mentions légales (ce refus ne couvre pas les pages de campagne, qui respectent le réglage « Ne pas suivre » de votre navigateur).
                </p>

                <h2 className="legal-section-title">Vos droits</h2>
                <p className="legal-paragraph">
                    Conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'effacement et d'opposition sur vos données. Pour l'exercer : <strong>sappm@pm.gouv.fr</strong>. Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
                </p>

                <h2 className="legal-section-title">Avertissement spécifique — questionnaire GHQ-12</h2>
                <p className="legal-paragraph">
                    Même si aucune donnée n'est conservée ni transmise (le score n'est vu que par la personne qui répond), un avertissement reste nécessaire — non pas pour une raison de protection des données, mais de protection de la personne au moment du résultat :
                </p>
                <ul className="legal-list">
                    <li>Avant le test : rappel que c'est un outil informatif et non diagnostique, et qu'aucune donnée n'est conservée ni transmise à un tiers.</li>
                    <li>Après le test, à tous les paliers de score : rappel qu'un professionnel de santé reste l'interlocuteur adapté pour toute question sur son état.</li>
                    <li>Au palier de score le plus élevé : ressources concrètes et actionnables (psycom.org, 3114).</li>
                </ul>

                <h2 className="legal-section-title">Outils d'auto-évaluation (QVT, GHQ-12)</h2>
                <p className="legal-paragraph">
                    Les questionnaires de bien-être et de santé mentale (GHQ-12) sont anonymes, ne nécessitent aucune inscription, et leurs résultats individuels ne sont ni stockés nominativement ni transmis à un tiers. Ce sont des outils informatifs, non diagnostiques — en cas de score signalant une détresse importante, une orientation vers des ressources professionnelles est proposée (ex. psycom.org, 3114).
                </p>

                <h2 className="legal-section-title">Sécurité</h2>
                <p className="legal-paragraph">
                    Le site est servi en connexion chiffrée (HTTPS). Les mots de passe sont hashés avec salage (bcrypt) avant stockage en base — jamais stockés en clair. L'accès à la création de compte est restreint aux domaines e-mail professionnels des services concernés.
                </p>
            </div>
        </div>
    );
}
